import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const MySuggestionsPage = () => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/call-suggestions/my');
      setSuggestions(response.data.suggestions);
    } catch (error) {
      toast.error('Erro ao carregar sugestões');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, hasDuplicate) => {
    if (status === 'pending') {
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
          <Clock className="h-3 w-3 mr-1" />
          Aguardando aprovação
        </Badge>
      );
    } else if (status === 'approved') {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          Aprovada e publicada
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          <XCircle className="h-3 w-3 mr-1" />
          Rejeitada
        </Badge>
      );
    }
  };

  const getQualisClass = (qualis) => `qualis-${qualis.toLowerCase()}`;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Minhas Sugestões</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <Button
            data-testid="new-suggestion-btn"
            onClick={() => navigate('/suggest-call')}
            className="bg-[#002FA7] hover:bg-[#001f75]"
          >
            Nova Sugestão
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Carregando...</div>
        ) : suggestions.length === 0 ? (
          <div className="bg-white border border-[#E4E4E7] rounded-md p-12 text-center">
            <p className="text-[#4B4C52] mb-4">Você ainda não sugeriu nenhuma chamada</p>
            <Button onClick={() => navigate('/suggest-call')}>Fazer Primeira Sugestão</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {suggestions.map(suggestion => (
              <div
                key={suggestion.suggestion_id}
                data-testid={`suggestion-${suggestion.suggestion_id}`}
                className="bg-white border border-[#E4E4E7] rounded-md p-6"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold tracking-tight mb-2">{suggestion.title}</h3>
                    <p className="text-sm text-[#4B4C52] mb-3">{suggestion.journal_name}</p>
                  </div>
                  {getStatusBadge(suggestion.status, suggestion.has_duplicate)}
                </div>

                {suggestion.has_duplicate && suggestion.status === 'pending' && (
                  <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-md flex gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-orange-900">Possível chamada duplicada detectada. O admin irá verificar.</p>
                  </div>
                )}

                <p className="text-sm mb-4">{suggestion.description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge className={`${getQualisClass(suggestion.qualis_classification)} font-semibold`}>
                    QUALIS {suggestion.qualis_classification}
                  </Badge>
                  <Badge variant="outline">{suggestion.publication_type}</Badge>
                  {suggestion.is_free && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Gratuito
                    </Badge>
                  )}
                </div>

                <div className="text-sm text-[#4B4C52]">
                  <p>Prazo: {formatDate(suggestion.deadline)}</p>
                  <p>Enviada em: {formatDate(suggestion.created_at)}</p>
                </div>

                {suggestion.status === 'rejected' && suggestion.admin_note && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-900">
                      <strong>Motivo da rejeição:</strong> {suggestion.admin_note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySuggestionsPage;
